import { LoadingButton } from "@mui/lab";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ChipRadioGroup from "../common/RadioChip/RadioChip";
import { ClientType } from "../../types/genericType";
import "./styles.scss";
import { createClient, editClient } from "../../services/clientService";
import AddressComp, { Address } from "../addressComp/AddressComp";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { taxandSSNFormatter } from "../../utils/utils";

type NewClientProps = {
  setOnTxnSuccess: Function;
  setSnackbarMsg: Function;
  getClientsData: Function;
  editFlow?: boolean;
  selectedClient: ClientType;
  handleDrawerClose: Function;
  isActive?: boolean;
};

const NewClient = (props: NewClientProps) => {
  const {
    setOnTxnSuccess,
    setSnackbarMsg,
    getClientsData,
    editFlow,
    selectedClient,
    handleDrawerClose,
    isActive,
  } = props;
  const [firstName, setFirstName] = useState<string>(
    editFlow ? selectedClient?.firstName : ""
  );
  const [lastName, setLastName] = useState<string>(
    editFlow ? selectedClient?.lastName : ""
  );
  const [email, setEmail] = useState<string | undefined>(
    editFlow ? selectedClient?.email ?? undefined : ""
  );
  const [type, setType] = useState<string>(
    editFlow ? selectedClient?.type : "Individual"
  );
  const [taxNumber, setTaxNumber] = useState<string>(
    editFlow ? taxandSSNFormatter(selectedClient?.type,selectedClient?.taxNumber) ?? "" : ""
  );
  const [companyName, setCompanyName] = useState<string | null>(
    editFlow ? selectedClient?.companyName ?? null : null
  );
  const [addressObj, setAddressObj] = useState<Address>({
    address: editFlow ? selectedClient?.address : "",
    apartment: editFlow ? selectedClient?.apartment : "",
    city: editFlow ? selectedClient?.city : "",
    state: editFlow ? selectedClient?.state : "",
    zip: editFlow ? selectedClient?.zip : "",
    country: editFlow ? selectedClient?.country : "USA",
  });
  const [mobile, setMobile] = useState<string | null>(
    editFlow
      ? selectedClient?.mobile &&
          selectedClient?.mobile?.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
      : null
  );
  const [officePhone, setOfficePhone] = useState<string | null>(
    editFlow
      ? selectedClient?.officePhone &&
          selectedClient?.officePhone?.replace(
            /(\d{3})(\d{3})(\d{4})/,
            "($1) $2-$3"
          )
      : null
  );
  const [homePhone, setHomePhone] = useState<string | null>(
    editFlow
      ? selectedClient?.homePhone &&
          selectedClient?.homePhone?.replace(
            /(\d{3})(\d{3})(\d{4})/,
            "($1) $2-$3"
          )
      : null
  );
  const [fax, setFax] = useState<string | null>(
    editFlow
      ? selectedClient?.fax &&
          selectedClient?.fax?.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
      : ""
  );
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    _event: React.MouseEvent<HTMLButtonElement>
  ) => {
    _event.preventDefault();
  };

  const handleNumberFieldChange = (val: string, updateFunction: Function) => {
    const numericValue = val.replace(/[^0-9]/g, "");
    const formattedValue = numericValue === "" ? null : numericValue.replace(
      /(\d{3})(\d{3})(\d{4})/,
      "($1) $2-$3"
    );
    updateFunction(formattedValue);
  };

  const handleTaxIdChange = (val: string) => {
    const numericValue = val.replace(/[^0-9]/g, "");
    let formattedValue = taxandSSNFormatter(type,numericValue);
    if(formattedValue){
      setTaxNumber(formattedValue);
    }
  };

  useEffect(() => {
    !editFlow && setTaxNumber("");
  }, [type]);

  const handleCreateClient = () => {
    if (
      (type === "Individual" && !firstName) ||
      (type === "Individual" && !lastName)
    ) {
      setError("Please fill all the required fields");
      return;
    }
    const data: ClientType = {
      firstName,
      lastName,
      type: type === "Individual" ? "Individual" : "Company",
      taxNumber: taxNumber && taxNumber.replace(/[^0-9]/g, ""),
      companyName,
      email,
      ...addressObj,
      country: addressObj.country || "USA",
      mobile: mobile && mobile.replace(/[^0-9]/g, ""),
      officePhone: officePhone && officePhone.replace(/[^0-9]/g, ""),
      homePhone: homePhone && homePhone.replace(/[^0-9]/g, ""),
      fax: fax && fax.replace(/[^0-9]/g, ""),
    };
    setIsLoading(true);
    createClient(data)
      .then(() => {
        handleDrawerClose();
        setIsLoading(false);
        setOnTxnSuccess(true);
        setSnackbarMsg("Client created successfully");
        getClientsData();
      })
      .catch((err) => {
        setError(err?.response?.data?.message);
        setIsLoading(false);
        setOnTxnSuccess(false);
        setSnackbarMsg("Client creation failed");
      });
  };

  const handleEditClient = () => {
    if (
      (type === "Individual" && !firstName) ||
      (type === "Individual" && !lastName) ||
      (type === "Company" && !companyName)
    ) {
      setError("Please fill all the required fields");
      return;
    }
    const data: ClientType = {
      id: selectedClient.id,
      firstName,
      lastName,
      type: type === "Individual" ? "Individual" : "Company",
      companyName,
      email,
      ...addressObj,
      country: addressObj.country || "USA",
      mobile: !!mobile ? mobile.replace(/[^0-9]/g, "") : null,
      officePhone: !!officePhone ? officePhone.replace(/[^0-9]/g, "") : null,
      homePhone: !!homePhone ? homePhone.replace(/[^0-9]/g, "") : null,
      fax: !!fax ? fax.replace(/[^0-9]/g, "") : null,
      taxNumber: taxNumber && taxNumber.replace(/[^0-9]/g, ""),
      isActive,
    };
    setIsLoading(true);
    editClient(data)
      .then(() => {
        handleDrawerClose();
        setIsLoading(false);
        setOnTxnSuccess(true);
        setSnackbarMsg("Client created successfully");
        getClientsData();
      })
      .catch((err) => {
        setError(err?.response?.data?.message);
        setIsLoading(false);
        setOnTxnSuccess(false);
        setSnackbarMsg("Client creation failed");
      });
  };

  return (
    <Box className="new-client">
      <Stack spacing={3}>
        {editFlow ? (
          <>
            <Typography className="user-title">Client Type</Typography>
            <Typography className="user-type">{type}</Typography>
          </>
        ) : (
          <ChipRadioGroup
            label="Client Type"
            value={type}
            setValue={setType}
            options={["Individual", "Organization"]}
            disabled={editFlow}
          />
        )}
        {(type === "Organization" || type === "Company") && (
          <>
            <TextField
              id="outlined-helperText"
              label="Organization Name"
              size="small"
              required
              fullWidth
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value as string)}
            />
            <TextField
              id="outlined-helperText"
              label="Tax ID"
              disabled={editFlow ? (showPassword ? false : true) : false}
              size="small"
              fullWidth
              value={taxNumber}
              onChange={(event) =>
                handleTaxIdChange(event.target.value as string)
              }
              type={editFlow ? (showPassword ? "text" : "password") : "text"}
              InputProps={{
                inputProps: {
                  pattern: "[0-9]*",
                  maxLength: 11,
                },
                endAdornment: editFlow ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      className="password-toggle-icon"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </>
        )}
        <Stack direction="row" spacing={2}>
          <TextField
            id="outlined-helperText"
            label="First Name"
            required={type === "Individual"}
            size="small"
            fullWidth
            value={firstName}
            onChange={(event) => setFirstName(event.target.value as string)}
          />
          <TextField
            id="outlined-helperText"
            label="Last Name"
            size="small"
            required={type === "Individual"}
            fullWidth
            value={lastName}
            onChange={(event) => setLastName(event.target.value as string)}
          />
        </Stack>
        <Stack>
          <TextField
            id="outlined-helperText"
            label="Email"
            size="small"
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value as string)}
          />
        </Stack>
        {type === "Individual" && (
          <TextField
            id="outlined-helperText"
            label="SSN"
            size="small"
            fullWidth
            disabled={editFlow ? (showPassword ? false : true) : false}
            value={taxNumber}
            onChange={(event) =>
              handleTaxIdChange(event.target.value as string)
            }
            type={editFlow ? (showPassword ? "text" : "password") : "text"}
            InputProps={{
              inputProps: {
                pattern: "[0-9]*",
                maxLength: 11,
              },
              endAdornment: editFlow ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    className="password-toggle-icon"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}
        <AddressComp address={addressObj} setAddress={setAddressObj} />
        <Stack direction="row" spacing={2}>
          <TextField
            id="outlined-helperText"
            label="Mobile Phone"
            size="small"
            fullWidth
            value={mobile}
            onChange={(event) =>
              handleNumberFieldChange(event.target.value as string, setMobile)
            }
            InputProps={{
              inputProps: {
                pattern: "[0-9]*",
                maxLength: 14,
              },
            }}
          />
          <TextField
            id="outlined-helperText"
            label="Office Phone"
            size="small"
            fullWidth
            value={officePhone}
            onChange={(event) =>
              handleNumberFieldChange(
                event.target.value as string,
                setOfficePhone
              )
            }
            InputProps={{
              inputProps: {
                pattern: "[0-9]*",
                maxLength: 14,
              },
            }}
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <TextField
            id="outlined-helperText"
            label="Home Phone"
            size="small"
            fullWidth
            value={homePhone}
            onChange={(event) =>
              handleNumberFieldChange(
                event.target.value as string,
                setHomePhone
              )
            }
            InputProps={{
              inputProps: {
                pattern: "[0-9]*",
                maxLength: 14,
              },
            }}
          />
          <TextField
            id="outlined-helperText"
            label="Fax"
            size="small"
            fullWidth
            value={fax}
            onChange={(event) =>
              handleNumberFieldChange(event.target.value as string, setFax)
            }
            InputProps={{
              inputProps: {
                pattern: "[0-9]*",
                maxLength: 14,
              },
            }}
          />
        </Stack>
        {error && (
          <Typography variant="sFormHelper" className="error-text">
            {error}
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <LoadingButton
            variant="contained"
            data-testid="createTxnBtn"
            size="small"
            className="create-button"
            onClick={editFlow ? handleEditClient : handleCreateClient}
            loading={isLoading}
          >
            {editFlow ? "SAVE CHANGES" : "Create Client"}
          </LoadingButton>
          <Button className="cancel-button" onClick={() => handleDrawerClose()}>
            CANCEL
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default NewClient;
